/**
 * Order severity/priority values by the vendor's own rank.
 *
 * The Plan API already answers this question: `_load_priorities` in
 * service-rest-api/plan/get_suggestions.py returns the vendor's
 * priority labels sorted by their `rank`, and ships them to us as
 * `metadata.filters.bug[0].options`. The page used to discard that and
 * re-derive the values from the bug documents with a `localeCompare` natural
 * sort, which is only meaningful for vocabularies whose labels carry a numeric
 * prefix ("1-Blocking" … "5-Cosmetic"). Cisco's labels do not, so the sort
 * degraded to plain alphabetical: Catastrophic, Cosmetic, Enhancement, Minor,
 * Moderate, Severe.
 *
 * That mis-ordered the priority dropdown, and — because the severity
 * distribution bars are coloured purely by row position — painted Severe,
 * Moderate and Minor in the green band while Cosmetic and Enhancement sat in
 * orange. A panel that tells a reviewer their Severe bugs are the reassuring
 * ones is worse than no panel.
 *
 * @param values  the values actually present in the data
 * @param ranked  vendor labels in rank order (`filter.options`); may be absent
 * @returns the present values, ranked first in rank order, then any value the
 *          vendor's list does not mention, naturally sorted
 */

const naturalSort = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

const normalize = value =>
  String(value)
    .trim()
    .toLowerCase();

export function orderByRank(values, ranked) {
  if (!Array.isArray(values) || values.length === 0) return [];

  const rankOf = new Map();
  if (Array.isArray(ranked)) {
    // Normalized keys: the ranked labels come from vendor settings and the
    // values from bug documents. They are the same vocabulary, but a case or
    // whitespace difference must not silently demote a value to "unranked".
    ranked.forEach((label, index) => {
      const key = normalize(label);
      if (!rankOf.has(key)) rankOf.set(key, index);
    });
  }

  const isRanked = value => rankOf.has(normalize(value));

  // Anything the vendor's list omits stays visible rather than being dropped —
  // otherwise the distribution rows would sum to less than the headline count.
  const rankedValues = values.filter(isRanked).sort((a, b) => rankOf.get(normalize(a)) - rankOf.get(normalize(b)));
  const unrankedValues = values.filter(value => !isRanked(value)).sort(naturalSort);

  return [...rankedValues, ...unrankedValues];
}
